import { AWSFile } from "@/models/aws-file";
import {getFileUrl} from "@/utils/url";
import { InboxreaderLabel } from "./inboxreader-label";
import { IInboxDocumentInfo } from "./inboxreader-document-info";
import { printHumanFloat } from "@/utils/pretty-print";

export class InboxreaderPurchaseFile {
	ID:number = 0;
	PurchaseID:number = 0;
	FileID:number = 0;
	ThumbID:number = 0;
	File:AWSFile | null = null;
	Thumb:AWSFile | null = null;
	ThumbUrl:string = "";
	AiResult:string | null  = "";
	ManualResult:string | null = "";
	Processed:boolean = false;
	Approved:boolean = false;
	Labels:InboxreaderLabel[] = [];

	public constructor(data?:any) {
		if(!data) return;
		this.ID = data.ID;
		this.PurchaseID = data.PurchaseID;
		this.FileID = data.FileID;
		this.ThumbID = data.ThumbID;
		this.Processed = data.Processed;
		this.Approved = data.Approved;
		this.Labels = (data.Labels || [])
			.filter((l:any)=>l && l.Label)
			.map((l:any)=>new InboxreaderLabel(l.Label));
		if (data.File) {
			this.File = new AWSFile(data.File);
		}
		if (data.Thumb) {
			this.Thumb = new AWSFile(data.Thumb);
			this.ThumbUrl = getFileUrl(this.Thumb.Token);
		}
		if(data.AiResult){
			try {
				this.AiResult = JSON.parse(data.AiResult);
			} catch (e) {
				this.AiResult = null;
			}
		}
		if(data.ManualResult){
			try {
				this.ManualResult = JSON.parse(data.ManualResult);
			} catch (e) {
				this.ManualResult = null;
			}
		}
	}

	// getDocumentInfo returns what we know about the document. The values the
	// user confirmed himself (the app lets him correct the supplier, the date
	// and the total after a scan) take precedence over the recognised ones.
	getDocumentInfo():IInboxDocumentInfo | null{
		let ai = this.AiResult as unknown as IInboxDocumentInfo | null;
		let manual = this.ManualResult as unknown as IInboxDocumentInfo | null;
		if (!manual) return ai;
		if (!ai) return manual;
		let info = {...ai} as any;
		for (let key of Object.keys(manual)) {
			let value = (manual as any)[key];
			if (value !== "" && value != null) {
				info[key] = value;
			}
		}
		return info as IInboxDocumentInfo;
	}

	// getSupplierLabel returns the supplier we recognised. When we only know
	// its vat number we show that instead.
	getSupplierLabel():string{
		let info = this.getDocumentInfo();
		if (!info) return "";
		return info.Supplier || info.SupplierVAT || "";
	}

	getTotal():number | null{
		let info = this.getDocumentInfo();
		if (!info || !info.Total) return null;
		let total = parseFloat(info.Total);
		return isNaN(total) ? null : total;
	}

	getTotalLabel():string{
		let total = this.getTotal();
		if (total == null) return "";
		let info = this.getDocumentInfo();
		let amount = printHumanFloat(total);
		if (!info || !info.Currency || info.Currency == "EUR") return `€ ${amount}`;
		return `${info.Currency} ${amount}`;
	}

	// getInvoiceDate returns the date we read from the document, already
	// formatted as dd/mm/yyyy.
	getInvoiceDate():string{
		return this.getDocumentInfo()?.Date || "";
	}

	getDueDate():string{
		return this.getDocumentInfo()?.PayableByDate || "";
	}

	getName():string{
		if (!this.File) return "ERROR";
		return this.File.Name;
	}

	getThumbName():string{
		if (!this.Thumb) return "";
		return this.Thumb.Name;
	}

	getJSON():any{
		return {
			ID: this.ID,
			PurchaseID: this.PurchaseID,
			FileID: this.FileID,
			ThumbID: this.ThumbID,
			File: this.File,
			Thumb: this.Thumb,
			ThumbUrl: this.ThumbUrl,
			AiResult: this.AiResult,
			ManualResult: this.ManualResult,
			Processed: this.Processed,
			Approved: this.Approved,
			Labels: this.Labels.map(l=>l.getJSON()),
		};
	}

}